// General
import { gsap, Power4 } from "gsap";

import { ready } from "./lib/ready";

// Stimulus.js and controllers
import { Application } from "stimulus";
import ExpandableController from "./lib/expandable";
import NavMainSubMenuController from "./lib/nav-main-sub-menu-controller";
import HeaderController from "./lib/header-controller";
import SearchFieldController from "./lib/search-field-controller";
import AccordionController from "./lib/accordion-controller";
import AccordionItemController from "./lib/accordion-item-controller";
import DisruptorController from "./lib/disruptor-controller";

import Scene01Controller from "./scenes/scene-01";
import Scene02Controller from "./scenes/scene-02";
import Scene03Controller from "./scenes/scene-03";
import Scene04Controller from "./scenes/scene-04";
import Scene05Controller from "./scenes/scene-05";
import Scene06Controller from "./scenes/scene-06";

// Setup functions
import { InView } from "./lib/in-view";
import { AnchorScrollTo, scrollTo } from "./lib/anchor-scroll-to";
// import { SetupSwiper } from './lib/setup-swiper';
import { Lightbox } from "./lib/Lightbox";

// import TransitionMaskController from './lib/transition-mask-controller';
import OnPageNavController from "./lib/on-page-nav-controller";
import {
  ServicesController,
  ServicesColumnController,
  VideoController,
} from "./lib/controllers/services";
import { OffsetTeaserController } from "./lib/controllers/offset-teaser";
import HorizontalTeaserController from './lib/controllers/horizontal-teaser.js';

// fix iOS issues when resizing
import ScrollTrigger from "gsap/ScrollTrigger";
import { debounce } from "./lib/utils";
// import ScrollTrigger from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

// Docs: https://greensock.com/docs/v3/Plugins/ScrollTrigger/static.config()
ScrollTrigger.config({
  // autoRefreshEvents: "",
  // limitCallbacks: true,
  // the sync interval is as it seems like guilty for jumps on ipad
  syncInterval: 999999999,
  ignoreMobileResize: true,
});

Math.clamp = (num, min, max) => Math.min(Math.max(num, min), max);

Math.lerp = function (value1, value2, amount) {
  amount = amount < 0 ? 0 : amount;
  amount = amount > 1 ? 1 : amount;
  return value1 + (value2 - value1) * amount;
};

gsap.defaults({
  ease: Power4.easeInOut,
  duration: 0.35,
});

let application;

ready(function () {
  var isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
  if (isIE11) document.body.classList.add("-is-ie-11");

  // Stimulus.js and controllers
  application = Application.start();
  application.register("expandable", ExpandableController);
  application.register("nav-main-sub-menu", NavMainSubMenuController);
  application.register("header", HeaderController);
  application.register("search-field", SearchFieldController);
  application.register("accordion", AccordionController);
  application.register("accordion-item", AccordionItemController);
  application.register("disruptor", DisruptorController);
  application.register("scene-01", Scene01Controller);
  application.register("scene-02", Scene02Controller);
  application.register("scene-03", Scene03Controller);
  application.register("scene-04", Scene04Controller);
  application.register("scene-05", Scene05Controller);
  application.register("scene-06", Scene06Controller);
  // application.register('transition-mask', TransitionMaskController);
  application.register("on-page-nav", OnPageNavController);
  application.register("services", ServicesController);
  application.register("services-column", ServicesColumnController);
  application.register("offset-teaser", OffsetTeaserController);
  application.register("video", VideoController);
  application.register('horizontal-teaser', HorizontalTeaserController);
  // Setup functions
  InView();
  AnchorScrollTo(document.querySelector("header").offsetHeight * 1.5);
  // SetupSwiper();
  Lightbox();
});

/**
 * lazy loading for images
 */
function loadImagesDeferred(selector, conditionCallback) {
  var imagesToDefer = document.querySelectorAll(selector);
  for (var i = 0; i < imagesToDefer.length; i++) {
    const image = imagesToDefer[i];
    const condition = conditionCallback(image);
    if (!condition) continue;
    if (image.getAttribute("data-src")) {
      image.setAttribute("src", image.getAttribute("data-src"));
    }
  }
}

/**
 * lazy loading for videos
 */
function loadVideosDeferred(selector, dataAttrPathCallback) {
  var videosToDefer = document.querySelectorAll(selector);
  for (var i = 0; i < videosToDefer.length; i++) {
    const video = videosToDefer[i];
    if (window.innerWidth < 992 && video.getAttribute("data-poster")) {
      // replace with image
      const imageUrl = video.getAttribute("data-poster");
      const imgEl = document.createElement("img");
      imgEl.src = imageUrl;
      imgEl.setAttribute("alt", "");
      const parent = video.parentElement;
      parent.removeChild(video);
      parent.appendChild(imgEl);
    } else {
      // keep video
      const videoUrl = video.getAttribute("data-src");
      video.src = videoUrl;
    }
    const path = dataAttrPathCallback(video);
  }
}

/**
 * scroll when #anchor is set in URL
 */
const scrollToHash = function () {
  if (location.hash && location.hash !== "") {
    const targetEl = document.querySelector(location.hash);
    if (targetEl) {
      scrollTo(targetEl, 110);
    }
  }
};

function lazyImageLoading() {
  scrollToHash();

  loadImagesDeferred("img.scene06__image", () => window.innerWidth > 991);
  loadImagesDeferred(
    "img.scene06__mobile__person__image__img",
    () => window.innerWidth < 992
  );

  loadVideosDeferred(".scene03__background video", () =>
    window.innerWidth < 768 ? "data-src-sm" : "data-src"
  );
  loadVideosDeferred(".scene05__video__container video", () =>
    window.innerWidth < 768 ? "data-src-sm" : "data-src"
  );
  loadVideosDeferred(".scene06__video__container video", () =>
    window.innerWidth < 768 ? "data-src-sm" : "data-src"
  );
  loadVideosDeferred(".post-list__podcast__video video", () =>
    window.innerWidth < 768 ? "data-src-sm" : "data-src"
  );
}

// execute the lazy image loading "onload"
window.onload = lazyImageLoading;

// re-evaluate on resize
window.addEventListener("resize", lazyImageLoading);

/**
 * Below code handles resizing on desktop (always) and mobile (only width change, address bar changes are excluded)
 */
const deviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  } else if (
    /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua
    )
  ) {
    return "mobile";
  }
  return "desktop";
};

function isTouchDevice() {
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  );
}

let widthBefore = window.innerWidth;
function handleResize() {
  const device = deviceType();
  // desktops, that are not touch devices, refresh always, others only when the width changed
  const event = new Event("resize-recalculate");
  if (device === "desktop" && !isTouchDevice()) {
    // desktop - resize always
    ScrollTrigger.refresh();
    window.dispatchEvent(event);
  } else {
    // mobile and tablet - online resize on width change
    if (window.innerWidth !== widthBefore) {
      widthBefore = window.innerWidth;
      ScrollTrigger.refresh();
      window.dispatchEvent(event);
    }
  }
}

const debouncedResize = debounce(handleResize, 250);

window.addEventListener("resize", debouncedResize);
